import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import type { MeterForProductPageResponse } from '@vy/api-definition';
import { VyIcon } from '@vy/ui-design-system';
import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { VyDialog } from '../../../../../components/new-design/feedback/VyDialog';
import { useDeleteMeter } from '../../../../../hooks/requests/use-delete-meter';
import { useDialog } from '../../../../../hooks/use-dialog';
import { useMenu } from '../../../../../hooks/use-menu';
import { invalidateProductsQueries } from '../../../../../utils/query-invalidation';
import { runMutation } from '../../../../../utils/vy-mutation';

type MetersTableActionsProps = {
  meterId: string;
  row: MeterForProductPageResponse;
  disableDelete: boolean;
  meterName?: string;
};

export const MetersTableActions: React.FC<MetersTableActionsProps> = ({
  meterId,
  row,
  disableDelete,
  meterName,
}) => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const { anchorElement, isOpen, handleMenuOpen, handleMenuClose } = useMenu();

  const queryClient = useQueryClient();

  const deleteMeterMutation = useDeleteMeter({
    onSuccess: () => invalidateProductsQueries(queryClient),
  });

  const handleAction = (actionFn?: () => void) => {
    handleMenuClose();
    actionFn?.();
  };

  const onDelete = async () => {
    await runMutation(
      deleteMeterMutation,
      { meterId },
      {
        pending: 'Deleting meter...',
        success: 'Meter successfully deleted',
        error: 'Failed to delete meter',
      },
    );
  };

  return (
    <div>
      <VyDialog
        open={isDialogOpen}
        onClose={handleClose}
        label={`Delete ${meterName}?`}
        ctaText="Yes, delete"
        cancelButtonText="Cancel"
        onSubmit={onDelete}
        cancelButtonProps={{
          sx: {
            backgroundColor: 'rgba(13, 27, 39, 0.08))',
          },
        }}
        ctaButtonProps={{
          sx: {
            backgroundColor: '#FF2E57',
            '&:hover': {
              backgroundColor: '#c92041',
            },
          },
        }}
        dialogContentProps={{
          style: {
            maxWidth: '569px',
            minWidth: '569px',
          },
        }}
      >
        <Typography variant="body1">
          Are you sure you want to delete this metered product? This action is final and cannot be reversed.
        </Typography>
      </VyDialog>
      <IconButton size="medium" onClick={handleMenuOpen} disabled={false}>
        <VyIcon icon="DotsMenu" style={{ transform: 'rotate(90deg)' }} />
      </IconButton>

      <Menu
        anchorEl={anchorElement}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={isOpen}
        onClose={handleMenuClose}
      >
        <Link to={`/products/${meterId}/edit`} state={row}>
          <MenuItem>
            <ListItemIcon>
              <VyIcon icon="Edit" style={{ transform: 'scale(0.8)' }} />
            </ListItemIcon>
            <ListItemText> Edit</ListItemText>
          </MenuItem>
        </Link>

        <MenuItem onClick={() => handleAction(handleOpen)} disabled={disableDelete}>
          <ListItemIcon>
            <VyIcon icon="Delete" style={{ transform: 'scale(0.8)' }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
};
